import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  CheckCircleIcon,
  CalendarIcon,
  MapPinIcon,
  EnvelopeIcon,
  CreditCardIcon,
  PrinterIcon,
  HomeIcon
} from '@heroicons/react/24/outline';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

interface SuccessPageData {
  registrationId: string;
  primarySponsor: string;
  contactInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  companyInfo: {
    companyName: string;
    companyLogo?: string;
  };
  billingInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
  totalAmount: number;
  teamName?: string;
  players?: Array<{
    firstName: string;
    lastName: string;
    needsGolfClubs?: '' | 'yes' | 'no';
    golfClubHandedness?: '' | 'left' | 'right';
  }>;
  primaryPlayerNeedsGolfClubs?: '' | 'yes' | 'no';
  primaryPlayerGolfClubHandedness?: '' | 'left' | 'right';
  dinnerGuests?: Array<{firstName: string; lastName: string}>;
  usePrimaryAsPlayer1?: boolean;
  usePrimaryAsGuest1?: boolean;
  specialNotes?: string;
  confirmedAdditionalSponsorships?: any[];
  confirmedAlaCarteItem?: any;
  confirmedAlaCarteQuantity?: any;
  smsUpdates?: boolean;
  paymentUrl?: string;
  paymentLinkWarning?: string;
}

const RegistrationSuccessPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const data = location.state as SuccessPageData;
  const eventYear = new Date().getFullYear();

  // Additional sponsorship options (same as RegistrationPage)
  const additionalSponsorships = [
    { 
      id: 'beverage-cart', 
      name: 'Beverage Cart Sponsor', 
      price: 5000, 
      description: 'Mobile beverage service throughout the course',
      benefits: [
        'Logo/name displayed on all beverage carts',
        'Option for sponsored hole'
      ]
    },
    { 
      id: 'bloody-mary', 
      name: 'Bloody Mary Breakfast Sponsor', 
      price: 5000, 
      description: 'Sponsor the pre-tournament breakfast beverage station',
      benefits: [
        'Logo/name displayed at breakfast beverage station',
        'Recognition during breakfast announcements'
      ]
    },
    { 
      id: 'food-truck', 
      name: 'Food Truck Sponsor', 
      price: 5000, 
      description: 'On-course mobile food service provider',
      benefits: [
        'Logo/name displayed at the food truck',
        'Option for a table at the food truck'
      ]
    },
    { 
      id: 'air-cannon', 
      name: 'Air Cannon Sponsor', 
      price: 3000, 
      description: 'Sponsor the exciting tee shot air cannon experience',
      benefits: [
        'Logo/name displayed at the air cannon hole'
      ]
    },
    { 
      id: 'cigar-bar', 
      name: 'Cigar Bar Sponsor', 
      price: 3000, 
      description: 'Premium cigar station for participants',
      benefits: [
        'Logo/name displayed at the cigar bar'
      ]
    },
    { 
      id: 'photography', 
      name: 'Photography Sponsor', 
      price: 3000, 
      description: 'Professional event photography services',
      benefits: [
        'Logo/name displayed on the photo frames alongside Crash Champions'
      ]
    },
    { 
      id: 'raffle-prize', 
      name: 'Raffle Prize Sponsor', 
      price: 3000, 
      description: 'Provide prizes for tournament raffle drawings',
      benefits: [
        'Logo/name displayed on tables at reception'
      ]
    },
    { 
      id: 'beverage-station', 
      name: 'Beverage Station Sponsor', 
      price: 3000, 
      description: 'Fixed beverage station on the course',
      benefits: [
        'Logo/name display on bars at reception'
      ]
    },
    { 
      id: 'golf-cart', 
      name: 'Golf Cart Sponsor', 
      price: 3000, 
      description: 'Tournament golf cart fleet sponsorship',
      benefits: [
        'Logo/name displayed on golf carts'
      ]
    },
    { 
      id: 'premier-hole', 
      name: 'Premier Hole Sponsor', 
      price: 2000, 
      description: 'Sponsor a featured hole with signage and setup',
      benefits: [
        'Logo/name displayed on hole and vendor setup'
      ]
    },
    { 
      id: 'driving-range', 
      name: 'Driving Range Sponsor', 
      price: 1500, 
      description: 'Practice facility sponsorship and signage',
      benefits: [
        'Logo/name display at driving range'
      ]
    },
    { 
      id: 'closest-pin', 
      name: 'Closest to the Pin Sponsor', 
      price: 1500, 
      description: 'Sponsor the closest to pin contest',
      benefits: [
        'Logo/name displayed on hole'
      ]
    },
    { 
      id: 'longest-drive', 
      name: 'Longest Drive Sponsor', 
      price: 1500, 
      description: 'Sponsor the longest drive competition',
      benefits: [
        'Logo/name displayed on hole'
      ]
    }
  ];

  // Map confirmed additional sponsorships to actual sponsorship objects
  const additionalSponsors = (data.confirmedAdditionalSponsorships || [])
    .map((id: any) => additionalSponsorships.find(s => s.id === id))
    .filter(Boolean); // Remove any undefined values

  const golferLineItemAmount = data.confirmedAlaCarteItem?.id === 'add-golfers'
    ? (data.confirmedAlaCarteQuantity?.price || data.totalAmount)
    : 0;
  const hasAdditionalLineItems = additionalSponsors.length > 0 || golferLineItemAmount > 0;

  // Handle print function
  const handlePrint = () => {
    window.print();
  };

  // Handle navigate to home
  const handleReturnHome = () => {
    navigate('/');
  };

  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentTraceId, setPaymentTraceId] = useState<string | null>(null);
  const [paymentExchangeId, setPaymentExchangeId] = useState<string | null>(null);

  // Open Clearent hosted payment page in a new tab
  const handleCompletePayment = async () => {
    if (paymentProcessing) return;

    let paymentWindow: Window | null = null;

    try {
      setPaymentProcessing(true);
      setPaymentError(null);
      setPaymentSuccess(false);
      setPaymentTraceId(null);
      setPaymentExchangeId(null);

      // Store registration data in sessionStorage so payment callback page can access it
      sessionStorage.setItem(
        `registration_${data.registrationId}`,
        JSON.stringify({
          registrationId: data.registrationId,
          contactInfo: data.contactInfo,
          billingInfo: data.billingInfo,
          primarySponsor: data.primarySponsor,
          totalAmount: data.totalAmount,
          teamName: data.teamName,
        })
      );

      paymentWindow = window.open('about:blank', '_blank');
      if (!paymentWindow) {
        throw new Error('Popup was blocked. Please allow popups for this site and try again.');
      }

      paymentWindow.document.title = 'Opening Secure Payment...';
      paymentWindow.document.body.innerHTML = '<p style="font-family: Arial, sans-serif; padding: 24px;">Opening secure payment page...</p>';

      const isHostedPaylinkUrl = (value: string) => /^https:\/\/(gateway\.clearent\.net|gateway-(int|sb|prod)\.clearent\.net)\/paylink\//i.test(value);

      if (data.paymentUrl && isHostedPaylinkUrl(data.paymentUrl)) {
        paymentWindow.location.replace(data.paymentUrl);
        setPaymentSuccess(true);
        return;
      }

      const response = await fetch('/api/generate-payment-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: data.totalAmount,
          invoice: data.registrationId,
          orderId: data.registrationId,
          customerId: data.registrationId,
          description: `Champions Classic ${eventYear} - ${data.primarySponsor} Registration`,
          emailAddress: data.contactInfo.email,
          phone: data.contactInfo.phone,
          billingFirstName: data.billingInfo.firstName,
          billingLastName: data.billingInfo.lastName,
          billingAddress: data.billingInfo.address,
          billingCity: data.billingInfo.city,
          billingState: data.billingInfo.state,
          billingZip: data.billingInfo.zipCode,
          showTipAmount: 'false',
          requireBillingAddress: 'true',
          showBillingAddress: 'true',
          createToken: 'false',
          enableAch: 'false',
          requireEmailAddress: 'true',
          requirePhone: 'true',
        }),
      });

      let result: any = null;
      try {
        result = await response.json();
      } catch {
        result = null;
      }

      const paymentLink = result?.paymentLink;
      const traceId = result?.traceId || null;
      const exchangeId =
        result?.details?.['exchange-id']
        || result?.details?.payload?.['exchange-id']
        || (Array.isArray(result?.attempts)
          ? (result.attempts.find((a: any) => !!a?.exchangeId)?.exchangeId || null)
          : null);
      const hasValidHostedLink = typeof paymentLink === 'string' && isHostedPaylinkUrl(paymentLink);

      if (!response.ok || !hasValidHostedLink) {
        const msg = result?.userMessage || result?.error || 'Failed to generate hosted payment link.';
        const err: any = new Error(msg);
        err.traceId = traceId;
        err.exchangeId = exchangeId;
        throw err;
      }

      paymentWindow.location.replace(paymentLink);
      setPaymentSuccess(true);
    } catch (error: any) {
      if (paymentWindow && !paymentWindow.closed) {
        paymentWindow.close();
      }
      const traceId = error?.traceId || null;
      const exchangeId = error?.exchangeId || null;
      setPaymentTraceId(traceId);
      setPaymentExchangeId(exchangeId);
      setPaymentError(error?.message || 'Unable to open payment link right now.');
    } finally {
      setPaymentProcessing(false);
    }
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  // Handle redirect if no valid registration state
  React.useEffect(() => {
    if (!data?.registrationId) {
      navigate('/', { replace: true });
    }
  }, [navigate, data?.registrationId]);

  if (!data?.registrationId) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="print:hidden">
        <Navigation />
      </div>
      
      {/* Print-Only Summary */}
      <div className="print:block hidden print:p-6 print:bg-white print:text-black">
        <div className="print:max-w-4xl print:mx-auto print:text-sm">
          {/* Print Header */}
          <div className="print:text-center print:mb-6 print:border-b print:border-gray-300 print:pb-4">
            <div className="print:flex print:items-center print:justify-center print:mb-3">
              <img src={`${process.env.PUBLIC_URL}/assets/CrashChampions_Logo_White.png`} alt="Logo" />
            </div>
            <h1 className="print:text-2xl print:font-bold print:text-gray-900 print:mb-2">
              Champions Classic 2026 - Registration Summary
            </h1>
            <p className="print:text-base print:text-gray-700">
              Thursday, September 10, 2026 @ Bolingbrook Golf Club
            </p>
            <p className="print:text-xs print:text-green-600 print:font-semibold print:mt-2">
              Registration ID: {data.registrationId}
            </p>
          </div>

          {/* Print Registration Details */}
          <div className="print:grid print:grid-cols-2 print:gap-6 print:mb-6">
            <div>
              <h3 className="print:text-base print:font-bold print:text-gray-900 print:mb-3 print:border-b print:border-gray-200 print:pb-2">
                Registration Details
              </h3>
              <div className="print:space-y-2">
                <div>
                  <p className="print:font-semibold print:text-gray-700 print:text-xs">Company:</p>
                  <p className="print:text-gray-900 print:text-xs">{data.companyInfo.companyName}</p>
                </div>
                {data.teamName && (
                  <div>
                    <p className="print:font-semibold print:text-gray-700 print:text-xs">Team Name:</p>
                    <p className="print:text-gray-900 print:text-xs">{data.teamName}</p>
                  </div>
                )}
                <div>
                  <p className="print:font-semibold print:text-gray-700 print:text-xs">Sponsorship Level:</p>
                  <p className="print:text-gray-900 print:text-xs">{data.primarySponsor}</p>
                </div>
                <div>
                  <p className="print:font-semibold print:text-gray-700 print:text-xs">Total Amount:</p>
                  <p className="print:text-gray-900 print:font-semibold print:text-xs">${data.totalAmount.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="print:text-base print:font-bold print:text-gray-900 print:mb-3 print:border-b print:border-gray-200 print:pb-2">
                Contact Information
              </h3>
              <div className="print:space-y-2">
                <div>
                  <p className="print:font-semibold print:text-gray-700 print:text-xs">Primary Contact:</p>
                  <p className="print:text-gray-900 print:text-xs">{data.contactInfo.firstName} {data.contactInfo.lastName}</p>
                  <p className="print:text-gray-700 print:text-xs">{data.contactInfo.email}</p>
                  <p className="print:text-gray-700 print:text-xs">{data.contactInfo.phone}</p>
                </div>
                <div>
                  <p className="print:font-semibold print:text-gray-700 print:text-xs">Address:</p>
                  <p className="print:text-gray-900 print:text-xs">{data.billingInfo.address}</p>
                  <p className="print:text-gray-900 print:text-xs">{data.billingInfo.city}, {data.billingInfo.state} {data.billingInfo.zipCode}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Print Tournament Information */}
          <div className="print:mb-6">
            <h3 className="print:text-base print:font-bold print:text-gray-900 print:mb-3 print:border-b print:border-gray-200 print:pb-2">
              Tournament Information
            </h3>
            <div className="print:grid print:grid-cols-2 print:gap-4">
              <div>
                <p className="print:font-semibold print:text-gray-700 print:mb-2 print:text-xs">Event Details:</p>
                <div className="print:space-y-1 print:text-xs">
                  <p>• Date: Thursday, September 10, 2026</p>
                  <p>• Check-In: 8:00 AM</p>
                  <p>• Shotgun Start: 10:00 AM</p>
                  <p>• Venue: Bolingbrook Golf Club</p>
                  <p>• Address: 2001 Rodeo Drive, Bolingbrook, IL 60490</p>
                </div>
              </div>
              <div>
                <p className="print:font-semibold print:text-gray-700 print:mb-2 print:text-xs">Tournament Format:</p>
                <div className="print:space-y-1 print:text-xs">
                  <p>• 4-Player Team Scramble format</p>
                  <p>• All carts and greens fees included</p>
                  <p>• Closest to pin and longest drive contests</p>
                  <p>• Post-round social hour and dinner</p>
                  <p>• Awards ceremony and auction</p>
                </div>
              </div>
            </div>
          </div>

          {/* Print Participants */}
          {(() => {
            const allPlayers = [
              ...(data.usePrimaryAsPlayer1 ? [{ firstName: data.contactInfo.firstName, lastName: data.contactInfo.lastName, needsGolfClubs: data.primaryPlayerNeedsGolfClubs || '', golfClubHandedness: data.primaryPlayerGolfClubHandedness || '' }] : []),
              ...(data.players || [])
            ];
            const allGuests = [
              ...(data.usePrimaryAsGuest1 ? [{ firstName: data.contactInfo.firstName, lastName: data.contactInfo.lastName }] : []),
              ...(data.dinnerGuests || [])
            ];
            if (allPlayers.length === 0 && allGuests.length === 0) return null;
            return (
              <div className="print:mb-6">
                <h3 className="print:text-base print:font-bold print:text-gray-900 print:mb-3 print:border-b print:border-gray-200 print:pb-2">
                  Participants
                </h3>
                <div className="print:grid print:grid-cols-2 print:gap-4">
                  {allPlayers.length > 0 && (
                    <div>
                      <p className="print:font-semibold print:text-gray-700 print:mb-2 print:text-xs">Golf Participants:</p>
                      <div className="print:space-y-1">
                        {allPlayers.map((player, index) => (
                          <p key={index} className="print:text-xs">
                            • {player.firstName} {player.lastName}
                            {player.needsGolfClubs === 'yes' && ` *Golf Clubs Needed${player.golfClubHandedness ? ` (${player.golfClubHandedness === 'left' ? 'Left-Hand' : 'Right-Hand'})` : ''}`}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  {allGuests.length > 0 && (
                    <div>
                      <p className="print:font-semibold print:text-gray-700 print:mb-2 print:text-xs">Dinner Guests:</p>
                      <div className="print:space-y-1">
                        {allGuests.map((guest, index) => (
                          <p key={index} className="print:text-xs">• {guest.firstName} {guest.lastName}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Print What to Bring */}
          <div className="print:mb-6">
            <h3 className="print:text-base print:font-bold print:text-gray-900 print:mb-3 print:border-b print:border-gray-200 print:pb-2">
              What to Bring
            </h3>
            <div className="print:grid print:grid-cols-2 print:gap-3">
              <div className="print:space-y-1 print:text-xs">
                <p>• Golf clubs and personal equipment</p>
                <p>• Comfortable golf attire (collared shirts required)</p>
                <p>• Golf shoes or appropriate footwear</p>
              </div>
              <div className="print:space-y-1 print:text-xs">
                <p>• Sunscreen and hat</p>
                <p>• Confirmation email or THIS summary (digital or printed)</p>
                <p>• Positive attitude and competitive spirit!</p>
              </div>
            </div>
          </div>

          {/* Print Additional Sponsorships */}
          {additionalSponsors && additionalSponsors.length > 0 && (
            <div className="print:mb-6">
              <h3 className="print:text-base print:font-bold print:text-gray-900 print:mb-3 print:border-b print:border-gray-200 print:pb-2">
                Additional Sponsorships
              </h3>
              <div className="print:space-y-1">
                {additionalSponsors.map((sponsorship: any, index: number) => (
                  <div key={index} className="print:flex print:justify-between">
                    <p className="print:text-xs">{sponsorship.name}</p>
                    <p className="print:text-xs print:font-semibold">${sponsorship.price?.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Print Footer */}
          <div className="print:text-center print:text-xs print:text-gray-600 print:border-t print:border-gray-300 print:pt-4">
            <p className="print:mb-2">
              <strong>Questions or Changes?</strong> Contact us at (815) 693-4361
            </p>
            <p>Email: charityevent@crashchampions.com</p>
            <p className="print:mt-3 print:text-xs print:text-gray-500">
              Please bring this summary with you to the tournament check-in
            </p>
          </div>
        </div>
      </div>

      {/* Screen Content */}
      <div className="print:hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 mt-20">
        {/* Success Message */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <CheckCircleIcon className="h-12 w-12 text-green-600" />
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Great! You're Almost Done.</h2>
          <p className="text-xl text-gray-600 mb-2">
            Thank you, <strong>{data.contactInfo.firstName} {data.contactInfo.lastName}</strong>, for registering for <strong>Champions Classic {eventYear}!</strong>
          </p>
          <p className="text-2xl text-gray-500 mb-6">
            Registration ID: <span className="font-mono font-semibold text-red-600">{data.registrationId}</span>
          </p>

          {/* Continue to Payment Button */}
          <div className="mb-8">
            <button
              onClick={handleCompletePayment}
              disabled={paymentProcessing}
              className="inline-flex items-center px-8 py-4 text-lg font-semibold rounded-xl transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <CreditCardIcon className="h-6 w-6 mr-3" />
              {paymentProcessing ? 'OPENING PAYMENT...' : 'COMPLETE PAYMENT'}
            </button>
            {paymentError && (
              <div className="mt-3 text-sm text-red-600">
                <p>{paymentError}</p>
                {paymentTraceId && (
                  <p className="mt-1 text-xs text-red-500">Reference ID: {paymentTraceId}</p>
                )}
                {paymentExchangeId && (
                  <p className="mt-1 text-xs text-red-500">Exchange ID: {paymentExchangeId}</p>
                )}
              </div>
            )}
            {paymentSuccess && (
              <p className="mt-3 text-sm text-green-600">Secure payment page opened in a new tab.</p>
            )}
            {!paymentError && !paymentSuccess && (
              <p className="mt-3 text-sm text-blue-600">A receipt email will be sent automatically after payment is processed.</p>
            )}
            <p className="mt-1 text-xs text-gray-600 font-medium">
              Upon finalization of payment, your spot will be confirmed for the tournament.
            </p>
            <div className="mt-3 flex flex-wrap justify-center gap-3">
              <a
                href={process.env.PUBLIC_URL + 'assets/forms/CIF-W9.pdf'}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="inline-flex items-center justify-center rounded-md bg-black px-6 py-3 text-[24px] font-semibold text-white transition-colors hover:bg-gray-900"
              >
                Download W9 Form
              </a>
              <a
                href={process.env.PUBLIC_URL + 'assets/forms/CIF-EIN.pdf'}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="inline-flex items-center justify-center rounded-md bg-black px-6 py-3 text-[24px] font-semibold text-white transition-colors hover:bg-gray-900"
              >
                Download EIN Form
              </a>
            </div>
          </div>
        </div>

        {/* Tournament Details */}
        <div className="bg-white rounded-xl shadow-sm border p-8 mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Tournament Details</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-4">Event Information</h4>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CalendarIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Thursday, September 10, 2026</p>
                    <p className="text-sm text-gray-600">Check-In: 8:00 AM | Shotgun Start: 10:00 AM</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Bolingbrook Golf Club</p>
                    <p className="text-sm text-gray-600">2001 Rodeo Drive<br />Bolingbrook, IL 60490</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Questions?</p>
                    <p className="text-sm text-gray-600">Contact us at charityevent@crashchampions.com</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-4">What's Next?</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  Check your email and/or phone for all tournament confirmation.
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  Tournament materials will be sent closer to the event date.
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  The day of the tournament, check in with registration using the above Registration ID.
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  Contact information updates can be made via email.
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Registration Details */}
        <div className="bg-white rounded-xl shadow-sm border p-8 mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Registration Summary</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Primary Selection */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-4">Primary Selection</h4>
              <div className="bg-red-50 rounded-lg p-4 mb-4">
                <p className="font-semibold text-red-800">{data.primarySponsor}</p>
              </div>
              <p className="text-sm text-gray-600 mb-4">Selection confirmed above.</p>
              <p className="text-lg text-red-700 font-semibold mb-4">Total: ${data.totalAmount.toLocaleString()}</p>

              {/* Company Information */}
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-500">Company</p>
                  <p className="text-gray-900">{data.companyInfo.companyName}</p>
                </div>
                {data.teamName && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">Team Name</p>
                    <p className="text-gray-900">{data.teamName}</p>
                  </div>
                )}
              </div>

              {/* Additional Selections */}
              {hasAdditionalLineItems && (
                <div className="mt-6">
                  <h5 className="text-md font-semibold text-gray-900 mb-3">Additional Selections</h5>
                  <div className="space-y-2">
                    {additionalSponsors.map((sponsorship: any, index: number) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-3">
                        <p className="text-sm font-medium text-gray-800">{sponsorship.name}</p>
                        <p className="text-xs text-gray-600">${sponsorship.price?.toLocaleString()}</p>
                      </div>
                    ))}
                    {golferLineItemAmount > 0 && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-sm font-medium text-gray-800">Golfer Cost</p>
                        <p className="text-xs text-gray-600">${golferLineItemAmount.toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Contact Information */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-4">Contact Information</h4>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-500">Primary Contact</p>
                  <p className="text-gray-900">{data.contactInfo.firstName} {data.contactInfo.lastName}</p>
                  <p className="text-sm text-gray-600">{data.contactInfo.email}</p>
                  <p className="text-sm text-gray-600">{data.contactInfo.phone}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Billing Address</p>
                  <p className="text-gray-900">{data.billingInfo.address}</p>
                  <p className="text-sm text-gray-600">
                    {data.billingInfo.city}, {data.billingInfo.state} {data.billingInfo.zipCode}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Details */}
          {(() => {
            const allPlayers = [
              ...(data.usePrimaryAsPlayer1 ? [{ firstName: data.contactInfo.firstName, lastName: data.contactInfo.lastName, needsGolfClubs: data.primaryPlayerNeedsGolfClubs || '', golfClubHandedness: data.primaryPlayerGolfClubHandedness || '' }] : []),
              ...(data.players || [])
            ];
            const allGuests = [
              ...(data.usePrimaryAsGuest1 ? [{ firstName: data.contactInfo.firstName, lastName: data.contactInfo.lastName }] : []),
              ...(data.dinnerGuests || [])
            ];
            return (
              <>
                {allPlayers.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Golf Participants</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {allPlayers.map((player, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-0.5">Player {index + 1}</p>
                          <p className="text-sm font-medium text-gray-900">{player.firstName} {player.lastName}</p>
                          {player.needsGolfClubs === 'yes' && (
                            <p className="text-xs text-red-700 mt-1">
                              *Golf Clubs Needed{player.golfClubHandedness ? ` (${player.golfClubHandedness === 'left' ? 'Left-Hand' : 'Right-Hand'})` : ''}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {allGuests.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Dinner Guests</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {allGuests.map((guest, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-0.5">Guest {index + 1}</p>
                          <p className="text-sm font-medium text-gray-900">{guest.firstName} {guest.lastName}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* Important Tournament Information */}
        <div className="bg-pink-50 border border-pink-200 rounded-xl p-8 mb-8">
          <h3 className="text-2xl font-bold text-pink-900 mb-6">IMPORTANT TOURNAMENT INFORMATION</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h4 className="text-lg font-semibold text-pink-900 mb-4">What to Bring</h4>
              <ul className="space-y-2 text-sm text-pink-800">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  Golf clubs and personal equipment
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  Comfortable golf attire (collared shirts required)
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  Golf shoes or appropriate footwear
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  Sunscreen and hat
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  Confirmation email (digital or printed)
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  Positive attitude and competitive spirit!
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-pink-900 mb-4">Tournament Format</h4>
              <ul className="space-y-2 text-sm text-pink-800">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  4-Player Team Scramble format
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  Shotgun start at 10:00 AM
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  All carts and greens fees included
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  Closest to pin and longest drive contests
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  Post-round social hour and dinner
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  Awards ceremony and auction
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
          <button
            onClick={handlePrint}
            className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-xl text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            <PrinterIcon className="h-5 w-5 mr-2" />
            PRINT SUMMARY
          </button>
          <button
            onClick={handleReturnHome}
            className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-xl text-white bg-gray-600 hover:bg-gray-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            <HomeIcon className="h-5 w-5 mr-2" />
            RETURN HOME
          </button>
        </div>



        {/* Contact for Changes */}
        <div className="text-center mb-8">
          <p className="text-sm text-gray-600">
            Need to make changes to your registration? Contact us at{' '}
            <a href="tel:+18156934361" className="text-red-600 hover:text-red-800 underline font-medium">
              (815) 693-4361
            </a>
            .
          </p>
        </div>
        </div>
      </div>

      <div className="print:hidden">
        <Footer />
      </div>
    </div>
  );
};

export default RegistrationSuccessPage;